import React from 'react';
import { useTranslation } from 'react-i18next';
import { Ticket } from 'lucide-react';

/**
 * Rifa/Bono card — product card with image + title + progress + price.
 *
 * Props:
 *   title       – rifa name
 *   price       – number | string (shown with $ prefix when number)
 *   percent     – 0..100 sold progress
 *   imageUrl    – optional background image URL; shows a tinted placeholder when absent
 *   onClick     – click handler (whole card is clickable)
 */
export default function BonoCard({ title, price, percent = 0, imageUrl, onClick }) {
  const { t } = useTranslation();
  const clampedPercent = Math.max(0, Math.min(100, Number(percent) || 0));
  const formattedPrice = typeof price === 'number' ? `$${price}` : price;

  return (
    <button
      type='button'
      onClick={onClick}
      className='group flex w-full flex-col overflow-hidden rounded-xl bg-surface text-left font-sans shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-ink/30'
    >
      {/* ── Image / placeholder ── */}
      <div className='relative aspect-[4/3] w-full overflow-hidden bg-neutral-200'>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className='absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105'
            loading='lazy'
          />
        ) : (
          <div className='absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-neutral-100 to-neutral-300'>
            <Ticket className='h-8 w-8 text-neutral-400' aria-hidden />
            <span className='line-clamp-2 max-w-[80%] text-center text-[10px] font-medium text-neutral-500'>
              {title}
            </span>
          </div>
        )}
      </div>

      {/* ── Body ── */}
      <div className='flex flex-1 flex-col gap-2 px-3 py-2.5'>
        <p className='line-clamp-2 text-sm font-semibold leading-snug text-ink'>{title}</p>

        {/* Progress */}
        <div className='space-y-1'>
          <div className='h-1.5 w-full rounded-full bg-divider'>
            <div
              className='h-1.5 rounded-full bg-ink transition-all duration-300'
              style={{ width: `${clampedPercent}%` }}
            />
          </div>
          <div className='flex items-center justify-between'>
            <span className='text-[10px] text-zinc-400'>
              {t('home.soldPercent', { percent: clampedPercent })}
            </span>
            {formattedPrice && <span className='text-sm font-bold text-ink'>{formattedPrice}</span>}
          </div>
        </div>
      </div>
    </button>
  );
}
